import { BaseCondition } from './base-condition.model';
import { Condition } from './condition.model';
import { CrmEntity } from '../../crm-entities/crm-entities.model';
import { TranslateService } from '@ngx-translate/core';

export abstract class CompositeCondition extends BaseCondition {
  childConditions: BaseCondition[] = [];
  booleanOperator: string;
  booleanOperatorHumanReadable: string;

  constructor(modelType: string) {
    super(modelType);
  }

  public toJSON(): any {
    const ret = super.toJSON();
    ret.childConditions = this.childConditions.map(child => child.toJSON());
    return ret;
  }

  cloneChildrenFromComposite(compositeToCloneFrom: CompositeCondition): Array<BaseCondition> {
    return compositeToCloneFrom.childConditions.map<BaseCondition>((childCondition: BaseCondition) => {
      const clone = childCondition.clone();
      clone.parent = this;
      return clone;
    });
  }

  getConditionsCount() {
    return this.childConditions.length;
  }

  addChildCondition(childCondition: BaseCondition) {
    this.childConditions.push(childCondition);
    // set the parent of the child to be me
    childCondition.parent = this;
  }

  insertChildConditionAtIndex(index: number, conditionToInsert: BaseCondition) {
    if (index < 0) {
      throw new Error('index out of bounds ' + index);
    }

    if (!conditionToInsert) {
      throw new Error('No condition to be added was provided');
    }

    this.childConditions.splice(index, 0, conditionToInsert);
    conditionToInsert.parent = this;
  }

  removeChildCondition(childCondition: BaseCondition) {
    const childIndex: number = this.childConditions.indexOf(childCondition);
    if (childIndex === -1) {
      throw new Error(`could not locate child condition in children`);
    }

    this.childConditions.splice(childIndex, 1);
    childCondition.parent = null;
  }

  generateExpression() {
    let resultExpression = '(';

    this.childConditions.forEach((childCondition, index: number) => {
      const childExpression = childCondition.generateExpression();

      resultExpression += childExpression;

      if (index < (this.childConditions.length - 1)) {
        resultExpression += ` ${this.booleanOperator} `;
      }
    });

    resultExpression += ')';
    return resultExpression;
  }

  getHeight(): number {
    // find the max height of a all of the defendantsa and add 1 to include me as the height.
    let maxChildHeight = 0;
    if (this.childConditions.length === 0) {
      return 0;
    }

    this.childConditions.forEach((childCondition) => {
      const currentChildHeight = childCondition.getHeight();
      if (currentChildHeight > maxChildHeight) {
        maxChildHeight = currentChildHeight;
      }
    });

    return maxChildHeight + 1;
  }

  generateHtmlForPreview(entities: CrmEntity[], translate: TranslateService): string {
    const html = this.childConditions.map(function (child, index) {
      return child.generateHtmlForPreview(entities, translate);
    });
    let generatedChildsHTML = ``;
    if (this.parent !== null) {
      generatedChildsHTML = `
            <div class="conditionsMainTable">
              <div class="conditions-container">
                <div class="conditionsOperator">
                 ${this.booleanOperatorHumanReadable}
                </div>
                <div class="conditionsExpression">
                 ${ html.join('')}
                </div>
              </div>
            </div>
            `;
    } else {
      generatedChildsHTML = `
            <div class="conditionsMainTable">
              <div class="parent-container">
               ${ html.join('')}
              </div>
            </div>
            `;
    }

    return generatedChildsHTML;
  }

  checkValidity(entities: CrmEntity[]): boolean {
    let isValid: boolean = true;

    this.childConditions.forEach((condition: BaseCondition) => {
      // need to check all conditions
      const isCurrentConditionValid = condition.checkValidity(entities);
      if (isCurrentConditionValid === false) {
        isValid = false;
      }
    });

    return isValid;
  }
}

export class AndCondition extends CompositeCondition {
  static fromJSON(json: any | string): AndCondition {
    if (typeof json === 'string') {
      // if it's a string, parse it first
      return JSON.parse(json, AndCondition.reviver);
    } else {
      // create an instance of the and-condition
      const andCondition = new AndCondition();
      // copy all the fields from the json object
      Object.assign(andCondition, json);
      andCondition.childConditions.map((child: BaseCondition) => child.parent = andCondition);
      return andCondition;
    }
  }

  static reviver(key: string, value: any): any {
    switch (key) {
      case '':
        return AndCondition.fromJSON(value);
      case 'childConditions':
        return value.map((conditionChild: any) => {
          switch (conditionChild.modelType) {
            case 'andCondition':
              return AndCondition.fromJSON(conditionChild);
            case 'orCondition':
              return OrCondition.fromJSON(conditionChild);
            default:
              return Condition.fromJSON(conditionChild);
          }
        });
      default:
        return value;
    }
  }

  constructor(andCondition?: AndCondition) {
    super('andCondition');
    this.booleanOperator = '&&';
    this.booleanOperatorHumanReadable = 'AND';

    if (andCondition) {
      this.childConditions = this.cloneChildrenFromComposite(andCondition);
    }
  }

  clone() {
    return new AndCondition(this);
  }
}

export class OrCondition extends CompositeCondition {

  static fromJSON(json: any | string): OrCondition {
    if (typeof json === 'string') {
      // if it's a string, parse it first
      return JSON.parse(json, OrCondition.reviver);
    } else {
      // create an instance of the or-condition
      const orCondition = new OrCondition();
      // copy all the fields from the json object
      Object.assign(orCondition, json);
      orCondition.childConditions.map((child: BaseCondition) => child.parent = orCondition);
      return orCondition;
    }
  }

  static reviver(key: string, value: any): any {
    switch (key) {
      case '':
        return OrCondition.fromJSON(value);
      case 'childConditions':
        return value.map((conditionChild: any) => {
          switch (conditionChild.modelType) {
            case 'andCondition':
              return AndCondition.fromJSON(conditionChild);
            case 'orCondition':
              return OrCondition.fromJSON(conditionChild);
            default:
              return Condition.fromJSON(conditionChild);
          }
        });
      default:
        return value;
    }
  }

  constructor(orCondition?: OrCondition) {
    super('orCondition');
    this.booleanOperator = '||';
    this.booleanOperatorHumanReadable = 'OR';

    if (orCondition) {
      this.childConditions = this.cloneChildrenFromComposite(orCondition);
    }
  }

  clone() {
    return new OrCondition(this);
  }
}

